import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import * as React from 'react';

import { AnalyticsStore, RouteStore } from 'bernie-core';
import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { MultiItemStore } from 'stores/multi-item-store';

import { GalleryViewModel, PriceViewModel, ShortlistModel } from 'models';
import { ReviewsViewModel } from 'models/hotel';

import { UitkCardMedia } from 'uitk-react-cards';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from 'uitk-react-images';

import { allHotelProductsSchema } from 'utils/uis-prime-micro-messages';

import { FavoriteButton } from 'components/common/shortlist/favorite-button';
import { GallerySwipe } from 'components/common/media/gallery-swipe';
import { QueuedImage } from 'components/common/media/queued-image';

export type LocalizedListingGalleryProps = LocalizedComponentProps & ListingGalleryProps;

export interface ListingGalleryProps {
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  gallery?: GalleryViewModel;
  hotelId: number;
  hotelName?: string;
  id: number;
  price?: PriceViewModel;
  reviews?: ReviewsViewModel;
  routeStore?: RouteStore;
  shortlistModel?: ShortlistModel;
  stars?: string;
  navigateOnClick?: boolean;
  hideGalleryTrigger?: boolean;
  hideImgNumber?: boolean;
  imgClickDestinationUrl?: string;
  history?: {
    push: (submitUrl: string) => void;
  };
  sendImgIndexInAnalytics?: boolean;
  ratio: UitkFigureAspectRatioType;
  listingClickHandler: () => void;
  multiItem?: MultiItemStore;
  onFavoriteClick?: () => void;
  mapListingView?: boolean;
  isSponsored?: boolean;
  pinned?: boolean;
  index: number;
}

export interface ListingGalleryState {
  currentGalleryIndex: number;
}

@withLocalization
@withRouter
@inject('analytics', 'currentHotel', 'routeStore', 'multiItem')
@observer
export class ListingGallery extends React.Component<LocalizedListingGalleryProps, ListingGalleryState> {
  constructor(props) {
    super(props);

    this.state = {
      currentGalleryIndex: 0
    };

    this.openPhotoGalleryDialog = this.openPhotoGalleryDialog.bind(this);
    this.updateIndex = this.updateIndex.bind(this);
    this.navigateToDestination = this.navigateToDestination.bind(this);
  }

  private openPhotoGalleryDialog(): void {
    this.props.routeStore.updateSearch({ pwaDialog: `photoGallery-${this.props.id}` });
    this.props.analytics.clickTracker('.LaunchMainPhotoGallery', 'Launch Photo Gallery From Image Click', null, () => null)();
  }

  private navigateToDestination(): void {
    this.props.listingClickHandler();

    const { analytics, history, imgClickDestinationUrl, hotelId, index, multiItem, pinned } = this.props;
    const { currentGalleryIndex } = this.state;
    const rfrr = pinned ? `:Pinned:${index + 1}` : `:HotelModule:${index + 1}`;
    const uisPrimeMessages = allHotelProductsSchema(hotelId, pinned);

    const rfrrs = [rfrr, `PHOTOS.${hotelId}.${this.props.gallery.images[currentGalleryIndex].subjectId}`];

    analytics.trackEvent(rfrrs.join(' '), 'Image clicked on HSR image gallery', analytics.omnitureData, null, null, uisPrimeMessages);

    if (multiItem && multiItem.isPackageShoppingPath()) {
      window.location.href = imgClickDestinationUrl;
    } else if (history) {
      history.push(imgClickDestinationUrl);
    }
  }

  private updateIndex(index: number): void {
    if (index >= 0) {
      this.setState({
        currentGalleryIndex: index
      });
    }
  }

  private createShortlistId(mapListingView: boolean, hotelId: number, isSponsored?: boolean): string {
    const shortlistId = `favorite-${hotelId}`;
    if (mapListingView) {
      return `map-card-favorite-${hotelId}`;
    }
    if (isSponsored) {
      return `sponsored-favorite-${hotelId}`;
    }

    return shortlistId;
  }

  public render() {
    const {
      gallery,
      hotelId,
      hotelName,
      l10n,
      shortlistModel,
      navigateOnClick,
      hideImgNumber,
      sendImgIndexInAnalytics,
      ratio,
      onFavoriteClick,
      mapListingView,
      isSponsored,
      pinned
    } = this.props;
    const { currentGalleryIndex } = this.state;
    const pinnedClasses = pinned ? 'uitk-flat-border-top' : '';

    return (
      <React.Fragment>
        {(!gallery || !gallery.images || !gallery.images.length) && (
          <UitkCardMedia>
            <UitkFigure>
              <UitkImage alt="" placeholderImage={false} className={pinnedClasses} />
            </UitkFigure>
          </UitkCardMedia>
        )}
        {gallery && gallery.images && gallery.images.length > 0 && (
          <span data-stid="listing-gallery">
            <UitkCardMedia className="title__media-container">
              <h2 className="is-visually-hidden">
                <LocalizedText message="hotels.infosite.gallery.a11yLabel" />
              </h2>
              <ErrorBoundary
                label="hotel.information.details.photoGallery"
                fallback={
                  <div className="photo-gallery">
                    <QueuedImage
                      className={`photo-gallery__image image-loader ${pinnedClasses}`}
                      src={gallery.images && gallery.images[0] && gallery.images[0].src}
                      useBackgroundImage={true}
                    />
                  </div>
                }
              >
                <GallerySwipe
                  galleryName="Main"
                  images={gallery.images}
                  currentIndex={currentGalleryIndex}
                  onIndexChange={this.updateIndex}
                  onImageClick={navigateOnClick ? this.navigateToDestination : this.openPhotoGalleryDialog}
                  hideImgNumber={hideImgNumber}
                  sendImgIndexInAnalytics={sendImgIndexInAnalytics}
                  ratio={ratio}
                  noScrim={true}
                  enablePreload={false}
                  pinned={pinned}
                />
                {shortlistModel && shortlistModel.meta && shortlistModel.meta.hotelId && (
                  <FavoriteButton
                    shortlistModel={shortlistModel}
                    labelSave={l10n.formatText('hotels.infosite.shortlist.saveToList.a11yLabel')}
                    labelRemove={l10n.formatText('hotels.infosite.shortlist.removeFromList.a11yLabel')}
                    category="hotel"
                    onSaveMessage={l10n.formatText('hotels.shortlist.saveToList.notification', hotelName)}
                    id={this.createShortlistId(mapListingView, hotelId, isSponsored)}
                    onClick={onFavoriteClick}
                  />
                )}
              </ErrorBoundary>
            </UitkCardMedia>
          </span>
        )}
      </React.Fragment>
    );
  }
}
